import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
/**
 * Copyright 2022, SumUp Ltd.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useEffect, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import styled from '../../styles/styled';
import { isFunction } from '../../util/type-check';
import { uniqueId } from '../../util/id';
import { MobileSidePanel } from './components/MobileSidePanel';
import { DesktopSidePanel } from './components/DesktopSidePanel';
import { Header } from './components/Header';
const BODY_OPEN_CLASS_NAME = 'ReactModal__SidePanel__Body--open';
export const HTML_OPEN_CLASS_NAME = 'ReactModal__SidePanel__Html--open';
export const PORTAL_CLASS_NAME = 'ReactModalPortal__SidePanel';
export const TRANSITION_DURATION_DESKTOP = 200;
export const TRANSITION_DURATION_MOBILE = 240;
export const SIDE_PANEL_WIDTH = '400px';
export const BODY_MAX_WIDTH = '480px';
const contentContainerStyles = ({ theme }) => css `
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 0;
  padding: env(safe-area-inset-top) env(safe-area-inset-right)
    env(safe-area-inset-bottom) env(safe-area-inset-left);
  width: 100%;
  height: 100%;

  ${theme.mq.mega} {
    padding-left: 0;
  }
`;
const contentContainerTopStyles = ({ top }) => top !== '0px' &&
    css `
    padding-top: 0;
  `;
const ContentContainer = styled.div(contentContainerStyles, contentContainerTopStyles);
const contentStyles = ({ theme }) => css `
  width: 100%;
  max-width: ${BODY_MAX_WIDTH};
  padding: 0 ${theme.spacings.mega};

  ${theme.mq.mega} {
    padding: 0 ${theme.spacings.giga};
  }
`;
const Content = styled.div(contentStyles);
export const SidePanel = ({ backButtonLabel, children, closeButtonLabel, headline, isBottomPanelClosing, isInstantOpen, isMobile, isStacked, onBack, onClose, top, ...props }) => {
    if (process.env.UNSAFE_DISABLE_ACCESSIBILITY_ERRORS !== 'true' &&
        process.env.NODE_ENV !== 'production' &&
        process.env.NODE_ENV !== 'test') {
        if (!closeButtonLabel) {
            throw new Error('The side panel is missing a `closeButtonLabel` prop. This is an accessibility requirement.');
        }
        if (onBack && !backButtonLabel) {
            throw new Error('The side panel is missing a `backButtonLabel` prop. This is an accessibility requirement.');
        }
    }
    const [isHeaderSticky, setHeaderSticky] = useState(false);
    const headerAriaId = useMemo(() => uniqueId('side-panel-header_'), []);
    useEffect(() => {
        setHeaderSticky(false);
    }, [isMobile]);
    const handleScroll = (event) => {
        setHeaderSticky(event.currentTarget.scrollTop > 0);
    };
    const defaultProps = {
        aria: {
            labelledby: headerAriaId,
        },
        bodyOpenClassName: BODY_OPEN_CLASS_NAME,
        htmlOpenClassName: HTML_OPEN_CLASS_NAME,
        onRequestClose: onBack || onClose,
        portalClassName: PORTAL_CLASS_NAME,
    };
    const content = (_jsxs(ContentContainer, { top: top, onScroll: handleScroll, children: [_jsx(Header, { backButtonLabel: backButtonLabel, closeButtonLabel: closeButtonLabel, headline: headline, id: headerAriaId, onBack: onBack, onClose: onClose, isSticky: isHeaderSticky }, void 0), _jsx(Content, { children: isFunction(children) ? children({ onBack, onClose }) : children }, void 0)] }, void 0));
    if (isMobile) {
        return (_jsx(MobileSidePanel, { ...defaultProps, ...props, isBottomPanelClosing: isBottomPanelClosing, isStacked: isStacked, children: content }, void 0));
    }
    return (_jsx(DesktopSidePanel, { ...defaultProps, ...props, isInstantOpen: isInstantOpen, top: top, children: content }, void 0));
};
